import { Fragment } from 'react';

import { useT } from './i18n-context';

import type { ReactElement, ReactNode } from 'react';

export interface TSlotsProps {
  /** The catalog key of the message containing `{slot}` placeholders. */
  readonly id: string;
  /** ReactNode per placeholder name — typically links or styled spans. */
  readonly slots: Readonly<Record<string, ReactNode>>;
}

/**
 * `<TSlots>` — rich-slot message rendering. Resolves a catalog message and
 * replaces each `{slot}` placeholder with the matching ReactNode, leaving the
 * surrounding text exactly as the translator authored it.
 *
 * This exists because composing "text + link + text" out of multiple catalog
 * keys joined with JSX `{' '}` bakes English word-spacing into the markup:
 * Hebrew attaches the ל/ול prepositions directly to the following word
 * ("לתנאי השירות"), which a space-joined `before`/`and`/`after` key triplet
 * can never produce (the billing consent line rendered "ל תנאי השירות" in
 * he until 2026-06-10). With a single slotted message, every locale controls
 * its own word order AND its own spacing around the embedded nodes.
 *
 * Unknown placeholders render verbatim (`{missing}`) — same developer-visible
 * gap convention as `t()` key-echoing.
 *
 * Lives beside `I18nProvider` (extracted 2026-07-02 when the provider grew
 * its async-catalog mode past the ~300-line file ceiling) and stays
 * re-exported from it, so existing `@/core/providers/I18nProvider` imports
 * keep working.
 */
export function TSlots({ id, slots }: TSlotsProps): ReactElement {
  const { t } = useT();
  const template = t(id);
  const parts = template.split(/(\{\w+\})/g);
  return (
    <>
      {parts.map((part, index) => {
        const name = /^\{(\w+)\}$/.exec(part)?.[1];
        const slot = name === undefined ? undefined : slots[name];
        // Index keys are safe here: the split of a fixed template is static.
        return <Fragment key={index}>{slot ?? part}</Fragment>;
      })}
    </>
  );
}
